import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/button/button';
import styles from './sessionNotice.module.scss';

export interface SessionNoticeProps {
  open: boolean;
  title: string;
  /** One paragraph per line — the reason line and the listings line read as separate facts. */
  lines: string[];
  dismissLabel: string;
  onDismiss: () => void;
}

/**
 * Tells the user their session was ended by an admin, and why.
 *
 * WHY THIS IS NOT `ConfirmDialog`
 * A confirm asks a question with two answers and resolves a promise. This asks
 * nothing — the session is already gone server-side and no answer would change
 * that. Bending ConfirmDialog into a one-button mode would make `cancelLabel`
 * conditionally meaningless for every existing call site.
 *
 * WHY IT IS DISMISSIBLE
 * Dismissing does not restore anything; it drops the user to guest, and the map
 * and listings are public. Trapping them behind a modal they cannot close would
 * claim the whole app is closed to them, which is not true.
 *
 * WHY IT IS NOT A TOAST
 * The app's toasts auto-dismiss after 3s. This is the one message the user must
 * act on — it explains why everything they try is failing — so it must not
 * disappear on a timer they might not be looking at.
 */
export function SessionNotice({ open, title, lines, dismissLabel, onDismiss }: SessionNoticeProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Same focus contract as confirmDialog: hand focus back to whatever had it,
  // or a keyboard user is stranded on <body> after dismissing.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    return () => opener?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  if (!open) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onDismiss}>
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        aria-describedby="sessionNoticeBody"
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.title}>{title}</h2>
        <div id="sessionNoticeBody" className={styles.body}>
          {lines.map((line) => (
            <p key={line} className={styles.line}>
              {line}
            </p>
          ))}
        </div>
        <div className={styles.actions}>
          <Button variant="primary" fullWidth autoFocus onClick={onDismiss}>
            {dismissLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
